import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { sprintService, type SprintGoalWithTasks } from '@/services/sprint.service';

interface MoveGoalToSprintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: SprintGoalWithTasks;
  currentSprintId: string;
  groupId: string;
}

const STATUS_COLORS: Record<string, string> = {
  planning: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  active: 'bg-green-500/10 text-green-600 border-green-500/20',
};

export function MoveGoalToSprintDialog({
  open, onOpenChange, goal, currentSprintId, groupId,
}: MoveGoalToSprintDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedSprintId, setSelectedSprintId] = useState('');
  const [moveTasks, setMoveTasks] = useState(true);

  // Fetch sprints in the same group, filtered to planning/active, excluding current
  const { data: sprints = [], isLoading: isLoadingSprints } = useQuery({
    queryKey: ['sprints-for-move', groupId],
    queryFn: () =>
      sprintService.list({ group_id: groupId, limit: 100 }).then((r) =>
        r.data.data.filter(
          (s) => s.id !== currentSprintId && ['planning', 'active'].includes(s.status)
        )
      ),
    enabled: open,
  });

  const moveMut = useMutation({
    mutationFn: () =>
      sprintService.moveGoal(currentSprintId, goal.id, {
        target_sprint_id: selectedSprintId,
        move_tasks: moveTasks,
      }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['sprint'] });
      queryClient.invalidateQueries({ queryKey: ['sprint-goals-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['sprint-tasks'] });
      const moved = res.data?.data?.tasks_moved ?? 0;
      toast({ title: `Goal moved with ${moved} task${moved !== 1 ? 's' : ''}` });
      handleClose();
    },
    onError: (e: any) =>
      toast({ title: 'Move failed', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  function handleClose() {
    setSelectedSprintId('');
    setMoveTasks(true);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Move Goal to Another Sprint
          </DialogTitle>
          <DialogDescription>
            Move this goal and optionally its linked tasks to a different sprint.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current goal info */}
          <div className="p-3 rounded-lg bg-muted/50 border">
            <p className="text-sm font-medium">{goal.title}</p>
            {goal.total_tasks > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {goal.total_tasks} task{goal.total_tasks > 1 ? 's' : ''} linked
              </p>
            )}
          </div>

          {/* Sprint selector */}
          <div className="space-y-2">
            <Label>Target Sprint</Label>
            {isLoadingSprints ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : sprints.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No available sprints in this group. Target sprints must be in Planning or Active status.
              </p>
            ) : (
              <Select value={selectedSprintId} onValueChange={setSelectedSprintId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a sprint..." />
                </SelectTrigger>
                <SelectContent>
                  {sprints.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <span>{s.name}</span>
                        <span className="text-xs text-muted-foreground">({s.reference_id})</span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0 h-4 ${STATUS_COLORS[s.status] || ''}`}
                        >
                          {s.status}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Move tasks toggle */}
          {goal.total_tasks > 0 && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="move-tasks"
                checked={moveTasks}
                onCheckedChange={(v) => setMoveTasks(!!v)}
              />
              <Label htmlFor="move-tasks" className="text-sm cursor-pointer">
                Also move {goal.total_tasks} linked task{goal.total_tasks > 1 ? 's' : ''}
              </Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={moveMut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => moveMut.mutate()}
            disabled={!selectedSprintId || moveMut.isPending}
          >
            {moveMut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Moving...
              </>
            ) : (
              <>
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Move Goal
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
